/**
 * Craps table rules. Same contract as the blackjack `TableRules`, the Hold'em,
 * baccarat and roulette ones: host-editable between rounds, parsed from
 * untrusted input and clamped, so the engine can read them as plain data.
 */

export interface CrapsRules {
  minBet: number;
  /** Cap on any single bet. Stakes accumulate per spot, so this bounds heat. */
  maxBet: number;
  /**
   * Odds multiple: pass odds are capped at the line bet times this, and lay
   * odds at the stake that *wins* the same amount. 3 is the common table sign.
   */
  maxOddsX: number;
  /** Host switches for the three optional bet groups on the layout. */
  propsEnabled: boolean;
  hardwaysEnabled: boolean;
  placeEnabled: boolean;
  /** Liberal field: 12 pays 3:1 instead of 2:1. */
  fieldTriple12: boolean;
  /** Auto-closes the come-out betting window when this expires. 0 disables. */
  betTimeoutMs: number;
  /** Auto-rolls for a stalled shooter when this expires. 0 disables. */
  rollTimeoutMs: number;
  /** How many seats one player may hold at once. */
  seatsPerPlayer: number;
  /** The table's buy-in, in chips. One bankroll follows a player across games. */
  startingChips: number;
  /** Lets a busted-out player top back up to `startingChips`. */
  rebuyAllowed: boolean;
}

export const DEFAULT_CRAPS_RULES: CrapsRules = {
  minBet: 5,
  maxBet: 500,
  maxOddsX: 3,
  propsEnabled: true,
  hardwaysEnabled: true,
  placeEnabled: true,
  fieldTriple12: false,
  // Short, because craps chips ride: a come-out window usually opens on a
  // spread that is already down, so it is a beat to adjust rather than a
  // window to build in. Ready still closes it early.
  betTimeoutMs: 15_000,
  rollTimeoutMs: 20_000,
  seatsPerPlayer: 1,
  startingChips: 1000,
  rebuyAllowed: true,
};

/** Bounds every numeric rule, so a hostile client cannot set 100x odds. */
export const CRAPS_RULE_LIMITS = {
  minBet: { min: 1, max: 1_000 },
  maxBet: { min: 1, max: 100_000 },
  maxOddsX: { min: 0, max: 100 },
  betTimeoutMs: { min: 0, max: 600_000 },
  rollTimeoutMs: { min: 0, max: 600_000 },
  seatsPerPlayer: { min: 1, max: 7 },
  startingChips: { min: 100, max: 1_000_000 },
} as const;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Same guarded parse as the other rule modules: only real numbers get through. */
const num = (raw: unknown, fallback: number): number => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : fallback;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

const int = (raw: unknown, fallback: number, key: keyof typeof CRAPS_RULE_LIMITS): number => {
  const { min, max } = CRAPS_RULE_LIMITS[key];
  return clamp(Math.round(num(raw, fallback)), min, max);
};

const bool = (raw: unknown, fallback: boolean): boolean =>
  typeof raw === 'boolean' ? raw : fallback;

/**
 * Fold an untrusted patch into the current rules. Unknown keys are dropped,
 * numbers are clamped, and the max bet can never fall below the min.
 */
export function normalizeCrapsRules(
  patch: Partial<Record<keyof CrapsRules, unknown>>,
  current: CrapsRules = DEFAULT_CRAPS_RULES,
): CrapsRules {
  const has = (key: keyof CrapsRules) => Object.hasOwn(patch, key);

  const minBet = has('minBet') ? int(patch.minBet, current.minBet, 'minBet') : current.minBet;
  // The ceiling can never fall below the floor, whichever of the two moved.
  const maxBet = Math.max(
    minBet,
    has('maxBet') ? int(patch.maxBet, current.maxBet, 'maxBet') : current.maxBet,
  );

  return {
    minBet,
    maxBet,
    maxOddsX: has('maxOddsX') ? int(patch.maxOddsX, current.maxOddsX, 'maxOddsX') : current.maxOddsX,
    propsEnabled: has('propsEnabled') ? bool(patch.propsEnabled, current.propsEnabled) : current.propsEnabled,
    hardwaysEnabled: has('hardwaysEnabled')
      ? bool(patch.hardwaysEnabled, current.hardwaysEnabled)
      : current.hardwaysEnabled,
    placeEnabled: has('placeEnabled') ? bool(patch.placeEnabled, current.placeEnabled) : current.placeEnabled,
    fieldTriple12: has('fieldTriple12')
      ? bool(patch.fieldTriple12, current.fieldTriple12)
      : current.fieldTriple12,
    betTimeoutMs: has('betTimeoutMs')
      ? int(patch.betTimeoutMs, current.betTimeoutMs, 'betTimeoutMs')
      : current.betTimeoutMs,
    rollTimeoutMs: has('rollTimeoutMs')
      ? int(patch.rollTimeoutMs, current.rollTimeoutMs, 'rollTimeoutMs')
      : current.rollTimeoutMs,
    seatsPerPlayer: has('seatsPerPlayer')
      ? int(patch.seatsPerPlayer, current.seatsPerPlayer, 'seatsPerPlayer')
      : current.seatsPerPlayer,
    startingChips: has('startingChips')
      ? int(patch.startingChips, current.startingChips, 'startingChips')
      : current.startingChips,
    rebuyAllowed: has('rebuyAllowed') ? bool(patch.rebuyAllowed, current.rebuyAllowed) : current.rebuyAllowed,
  };
}
